/* Gaani Suit Cutting Calculator — simple in, simple out.
   The visitor answers plain measurement questions; every rule of Gaani's
   course method runs here (the "backend"): derived cross measurements,
   back-neck shoulder adjustment, pleat allowances, salwar pieces, fabric.
   Output is a clean printable sheet. Client-side only — nothing uploaded. */
(function () {
  'use strict';
  var root = document.querySelector('[data-sg-calc]');
  if (!root) return;

  /* ---------- number helpers ---------- */
  function toNum(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }
  var EIGHTHS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];
  /* n to the nearest eighth, the way a tailor writes it: 6¼, ¾, 12 */
  function frac(n) {
    n = Math.round(n * 8) / 8;
    var whole = Math.floor(n), f = Math.round((n - whole) * 8);
    if (f === 8) { whole += 1; f = 0; }
    if (whole === 0 && f > 0) return EIGHTHS[f];
    return whole + (f ? EIGHTHS[f] : '');
  }
  /* Every inch value on the sheet goes through here, so they all carry the same
     ″ mark — fixed allowances (2½″) and ranges (7″–7½″) included. */
  function fmt(n) { return frac(n) + '″'; }

  /* Fabric: Gaani's amounts are in metres. Yards are added for shops that sell
     by the yard, rounded UP to the next ⅛ yd so the conversion is never short.
     No-break spaces keep each amount with its unit when a narrow phone wraps it. */
  var YD_PER_M = 1 / 0.9144;
  function yd(m) { return Math.ceil(m * YD_PER_M * 8 - 1e-9) / 8; }
  function fabric(lo, hi) {
    function span(f) { return f(lo) + (hi ? '–' + f(hi) : ''); }
    return span(frac) + '\u00a0m (' + span(function (m) { return frac(yd(m)); }) + '\u00a0yd)';
  }

  function field(id) { return root.querySelector('#sgc-' + id); }
  function val(id) { var el = field(id); return el ? toNum(el.value) : 0; }
  function str(id) { var el = field(id); return el ? el.value : ''; }

  /* "Measured from an existing suit" means these are half rounds (the suit laid
     flat), so the engine doubles them — and validation checks the doubled number. */
  var HALVES = ['bust', 'waist', 'hip', 'shoulderround'];
  function suitMode() { var el = field('mode'); return !!el && el.value === 'suit'; }
  function factorFor(el) { return suitMode() && HALVES.indexOf(el.id.replace('sgc-', '')) > -1 ? 2 : 1; }

  /* fine-tune boxes → model keys; blank = worked out from the other answers */
  var FINE_TUNE = { backacross: 'backAcross', frontacross: 'frontAcross', sideseam: 'sideSeam', dartpoint: 'dartPoint' };

  /* ---------- read the simple questions, derive the rest ---------- */
  function readForm() {
    var m = {
      name: str('name').trim(),
      length: val('length'), bust: val('bust'), waist: val('waist'), hip: val('hip'),
      teera: val('teera'), shoulderRound: val('shoulderround'),
      sleeveLen: val('sleevelen'), sleeveCuff: val('sleevecuff'),
      frontNeck: val('frontneck'), backNeck: val('backneck'),
      salLength: val('sallength'), salMohri: val('salmohri'),
      fabricWidth: str('fabwidth')
    };
    // fine-tune overrides (blank = Gaani's defaults, derived in the engine)
    Object.keys(FINE_TUNE).forEach(function (id) {
      var el = field(id);
      m[FINE_TUNE[id]] = (el && el.value !== '') ? toNum(el.value) : null;
    });
    if (suitMode()) { m.bust *= 2; m.waist *= 2; m.hip *= 2; m.shoulderRound *= 2; }
    var style = field('salstyle');
    m.salStyle = style ? style.value : 'semi';
    var plain = field('plain');
    m.pleats = !(plain && plain.checked);

    // derived per the course when not overridden
    if (m.backAcross == null) m.backAcross = m.teera - 2;      // back across ≈ teera − 2
    if (m.frontAcross == null) m.frontAcross = m.backAcross - 1; // front ≈ back − 1
    if (m.sideSeam == null) m.sideSeam = 14;                    // standard
    if (m.dartPoint == null) m.dartPoint = m.bust >= 40 ? 12 : 10; // heavier bust sits lower
    return m;
  }

  /* ---------- the engine (Gaani's formulas) ---------- */
  function compute(m) {
    var out = [];
    function group(title) { var g = { title: title, rows: [] }; out.push(g); return g; }
    function row(g, label, value) { g.rows.push([label, value]); }

    var k = group('Kameez');
    var heavier = Math.max(m.bust, m.hip);
    row(k, 'Cutting length (2 panels)', fmt(m.length + 3));
    row(k, 'Cutting width (4 folds)', fmt(heavier / 4 + 2));
    var adj = m.backNeck >= 5 ? 1.5 : (m.backNeck >= 3.5 ? 1 : 0.5);
    row(k, 'Tira — shoulder cut', fmt(m.teera / 2 - adj));
    row(k, 'Shoulder point (armhole)', fmt(m.shoulderRound <= 20 ? 7 : (m.shoulderRound <= 22 ? 7.5 : 8)));
    row(k, 'Back across mark', fmt(m.backAcross / 2));
    row(k, 'Front across mark', fmt(m.frontAcross / 2));
    row(k, 'Bust mark', fmt(m.bust / 4 + (m.pleats ? 0.125 : 0)));
    row(k, 'Waist mark', fmt(m.waist / 4 + (m.pleats ? 1 : 0)));
    row(k, 'Hip mark', fmt(m.hip / 4));
    row(k, 'Side seam', fmt(m.sideSeam));
    if (m.pleats) {
      row(k, 'Dart (apex) point', fmt(m.dartPoint + 0.5));
      row(k, 'Front pleat position', fmt((m.waist / 4 + 1) / 3));
      row(k, 'Side pleat', heavier >= 42 ? fmt(2.5) + ' × ' + fmt(2.5) : fmt(2) + ' × ' + fmt(2));
    }
    row(k, 'Back neck cut', fmt(2.5) + ' wide × ' + fmt(m.backNeck) + ' deep');
    row(k, 'Front shoulder cuts (front only)', fmt(1) + ' + ' + fmt(0.5) + ' down');

    var s = group('Sleeves');
    row(s, 'Cutting length', fmt(m.sleeveLen + 2));
    row(s, 'Width', fmt(m.shoulderRound / 2 + 0.5));
    row(s, 'Cuff strip', fmt(m.sleeveCuff + 2));
    row(s, 'Front kept at', fmt(m.sleeveLen >= 20 ? 4 : (m.sleeveLen >= 14 ? 5 : 7)));

    var w = group('Salwar' + (m.salStyle === 'full' ? ' — full Patiala' : m.salStyle === 'straight' ? ' — straight' : ' — semi Patiala'));
    if (m.salStyle === 'straight') {
      row(w, 'Cuff mark', fmt(m.salMohri + 0.5));
      row(w, 'Length (4 folds)', fmt(m.salLength - 4));
      row(w, 'Crotch (aasan), on the diagonal', fmt(9));
    } else {
      var beltReady = m.hip / 2 + 3;
      row(w, 'Belt — cut', fmt(beltReady + 1) + ' → ready ' + fmt(beltReady));
      row(w, 'Belt depth', fmt(7) + '–' + fmt(7.5) + ' → ready ' + fmt(5));
      row(w, 'Godde (2 knee panels)', fmt(m.salLength - 4) + ' × ' + fmt(m.salMohri - 1) + ' folded');
      row(w, 'Kunde (side panels)', fmt(m.salLength - 4) + ' long · slant ' + fmt(2.5));
      row(w, 'Aasan (crotch)', fmt(m.hip >= 44 ? 9.5 : 9));
      row(w, 'Mohri strips (2)', 'cut ' + fmt(10) + ' · mark at ' + fmt(m.salMohri + 1));
      row(w, 'Bottom allowance', fmt(2.5));
    }

    var f = group('Fabric to buy');
    row(f, 'Full suit (' + m.fabricWidth + '″ width)', m.fabricWidth === '60' ? fabric(3.5) : fabric(5));
    row(f, 'Salwar alone', m.salStyle === 'full' ? fabric(3) : (m.salStyle === 'semi' ? fabric(2.5) : (m.salLength > 36 ? fabric(2.5) : fabric(2, 2.25))));
    row(f, 'Also pick up', 'buckram + matching thread');

    return out;
  }

  /* ---------- render ---------- */
  function esc(t) { return String(t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function sheetHTML(groups) {
    var html = '';
    groups.forEach(function (g) {
      html += '<div class="sgc-group"><h3 class="sgc-group__title">' + esc(g.title) + '</h3><table class="sgc-table">';
      g.rows.forEach(function (r) {
        html += '<tr><td class="sgc-t-label">' + esc(r[0]) + '</td><td class="sgc-t-val">' + esc(r[1]) + '</td></tr>';
      });
      html += '</table></div>';
    });
    return html;
  }

  function whoLine(m) {
    var today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    return (m.name ? m.name + ' · ' : '') + today;
  }

  /* ---------- validation ----------
     A cutting sheet gets used on real fabric, so we never guess. Blank or
     out-of-range answers stop the sheet rather than silently becoming 0
     (which used to print negative and absurd cut numbers). Bounds are read
     straight off the inputs' own min/max so the markup stays the one source,
     and they are checked against the number the engine will actually use:
     after the suit-mode doubling, and after any blank fine-tune box is worked out. */
  function nameOf(el) { return el.getAttribute('data-sg-name') || 'this measurement'; }
  function plainNum(n) { return String(Math.round(n * 100) / 100); }
  function outside(n, el) {
    var min = parseFloat(el.min), max = parseFloat(el.max);
    return !isFinite(n) || (isFinite(min) && n < min) || (isFinite(max) && n > max);
  }

  function checkField(el) {
    var optional = el.hasAttribute('data-sg-num-optional');
    var factor = factorFor(el);
    var who = (factor === 2 ? 'half the suit’s ' : 'your ') + nameOf(el);
    var range = 'between ' + plainNum(parseFloat(el.min) / factor) + ' and ' + plainNum(parseFloat(el.max) / factor);
    if (el.validity && el.validity.badInput) return 'Type a plain number, like 15.5 for 15½. ' + who.charAt(0).toUpperCase() + who.slice(1) + ' should be ' + range + ' inches.';
    var raw = (el.value || '').trim();
    if (raw === '' && optional) return '';
    if (raw === '' || outside(parseFloat(raw) * factor, el)) {
      return (optional ? 'Leave ' + who + ' blank for Gaani’s standard, or enter it' : 'Enter ' + who) + ' in inches, ' + range + '.';
    }
    return '';
  }

  function checkWorkedOut(m) {
    var bad = [];
    Object.keys(FINE_TUNE).forEach(function (id) {
      var el = field(id);
      if (!el || el.value !== '') return; // typed in: already checked as entered
      var v = m[FINE_TUNE[id]];
      if (outside(v, el)) {
        setError(el, 'Worked out from your other answers this comes to ' + fmt(v) + ' — enter your ' + nameOf(el) +
          ' yourself, in inches, between ' + plainNum(parseFloat(el.min)) + ' and ' + plainNum(parseFloat(el.max)) + '.');
        bad.push(el);
      }
    });
    return bad;
  }

  function setError(el, msg) {
    var wrap = el.closest('.sg-calc__field') || el.parentElement;
    var id = el.id + '-err';
    var note = wrap.querySelector('.sg-calc__err');
    el.classList.toggle('is-invalid', !!msg);
    if (!msg) {
      el.removeAttribute('aria-invalid');
      el.removeAttribute('aria-describedby');
      if (note) note.remove();
      return;
    }
    if (!note) {
      note = document.createElement('span');
      note.className = 'sg-calc__err';
      note.id = id;
      wrap.appendChild(note);
    }
    note.textContent = msg;
    el.setAttribute('aria-invalid', 'true');
    el.setAttribute('aria-describedby', id);
  }

  function validate() {
    var bad = [];
    [].forEach.call(root.querySelectorAll('[data-sg-num], [data-sg-num-optional]'), function (el) {
      var msg = checkField(el);
      setError(el, msg);
      if (msg) bad.push(el);
    });
    return bad.length ? bad : checkWorkedOut(readForm());
  }

  /* ---------- step flow: questions → sheet ---------- */
  var stepInput = root.querySelector('[data-sg-step-input]');
  var stepResult = root.querySelector('[data-sg-step-result]');
  var sheetTitle = root.querySelector('[data-sg-sheet-title]');
  var alertBox = root.querySelector('[data-sg-errors]');
  var status = root.querySelector('[data-sg-status]');

  /* The sticky header must not swallow whatever we jump to. CSS gives fields and
     steps scroll-margin-top: var(--sgc-pin) + a little; this keeps --sgc-pin at
     the header's pinned height — or 0 when the page's own scroll-padding already
     clears the header (adding ours on top would double the gap). */
  function setPin() {
    var pin = 0;
    if (!(parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) > 0)) {
      var header = document.querySelector('.lectern-header');
      if (header && header.getAttribute('data-sticky') === 'true') pin = Math.ceil(header.getBoundingClientRect().height);
    }
    root.style.setProperty('--sgc-pin', pin + 'px');
  }
  function goTo(el) {
    setPin();
    el.scrollIntoView({ block: 'start' }); // smooth via the page's scroll-behavior, instant under reduced motion
  }
  setPin();
  var pinFrame;
  window.addEventListener('resize', function () {
    cancelAnimationFrame(pinFrame);
    pinFrame = requestAnimationFrame(setPin);
  });

  /* Polite announcement, a beat after the focus move so both get read. */
  var sayTimer;
  function announce(msg) {
    if (!status) return;
    clearTimeout(sayTimer);
    status.textContent = '';
    sayTimer = setTimeout(function () { status.textContent = msg; }, 150);
  }

  /* Printing: while a sheet is on screen <html> carries .sg-calc-print, and the
     section's print CSS hides everything except this section's sheet — header,
     footer, form, call-to-action. Same result from the Print button or Ctrl/Cmd+P. */
  var main = root.closest('main');
  if (main) {
    var host = root;
    while (host.parentElement && host.parentElement !== main) host = host.parentElement;
    host.classList.add('sg-calc-host');
  }
  function printMode(on) {
    if (main) document.documentElement.classList.toggle('sg-calc-print', on);
  }

  /* "One measurement needs a look: bust." / "3 … : bust, waist and side seam." /
     "12 measurements need a look, starting with kameez length." */
  function summary(bad) {
    var names = bad.map(nameOf);
    if (names.length === 1) return 'One measurement needs a look: ' + names[0] + '.';
    if (names.length > 3) return names.length + ' measurements need a look, starting with ' + names[0] + '.';
    return names.length + ' measurements need a look: ' + names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1] + '.';
  }

  function showProblems(bad) {
    if (alertBox) {
      alertBox.textContent = summary(bad);
      alertBox.hidden = false;
    }
    // A problem inside the closed Fine-tune drawer would stay invisible: open it first.
    bad.forEach(function (el) { var drawer = el.closest('details'); if (drawer) drawer.open = true; });
    goTo(bad[0].closest('.sg-calc__field') || bad[0]);
    bad[0].focus({ preventScroll: true });
  }

  /* A real submit, so Enter / Go in any field works as well as the button. */
  stepInput.addEventListener('submit', function (e) {
    e.preventDefault();
    var bad = validate();
    if (bad.length) { showProblems(bad); return; }
    if (alertBox) alertBox.hidden = true;
    var m = readForm();
    root.querySelector('[data-sg-sheet-body]').innerHTML = sheetHTML(compute(m));
    root.querySelector('[data-sg-sheet-who]').textContent = whoLine(m);
    stepInput.hidden = true;
    stepResult.hidden = false;
    printMode(true);
    goTo(stepResult);
    sheetTitle.focus({ preventScroll: true });
    announce('Your cutting sheet is ready' + (m.name ? ' for ' + m.name : '') + '.');
  });

  /* clear a field's complaint as soon as the visitor starts fixing it */
  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-sg-num], [data-sg-num-optional]') && e.target.classList.contains('is-invalid')) {
      setError(e.target, '');
      if (alertBox && !root.querySelector('.is-invalid')) alertBox.hidden = true;
    }
  });

  root.querySelector('[data-sg-edit]').addEventListener('click', function () {
    stepResult.hidden = true;
    stepInput.hidden = false;
    printMode(false);
    goTo(stepInput);
    var first = stepInput.querySelector('input, select');
    if (first) first.focus({ preventScroll: true });
    announce('Your measurements are still filled in. Change any of them and get a new sheet.');
  });

  /* print: the page's print CSS reduces it to the sheet alone (see printMode) */
  root.querySelector('[data-sg-print]').addEventListener('click', function () { window.print(); });
})();
